import * as yup from "yup";
import { ApolloError, NetworkStatus, useQuery } from "@apollo/client";
import { FormikHelpers, useFormik } from "formik";
import Image from "next/image";
import Link from "next/link";
import { ProfileType } from "../../types/ProfileType";
import SearchProfiles from "../../graphql/SearchProfiles.graphql";
import { Relay } from "../../types/Relay";
import { Space } from "../../types/Space";
import { constants } from "../../constants";
import getInitials from "../../utilities/GetInitials";
import getDefaultColour from "../../utilities/GetDefaultColour";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaw, faUser } from "@fortawesome/pro-regular-svg-icons";
import { useState } from 'react';

interface SearchFields {
    name: string;
    location: string;
    yearOfBirth: string;
    profileType: ProfileType;
}

interface FilterBuilderProps {
    yearOfBirth?: number;
    location?: string;
    profileType: ProfileType;
}

const SearchResultsSkeleton = ({ isLoadingDemoProfiles } : { isLoadingDemoProfiles : boolean }) => {
    return (
        <div>
            <div className="text-center my-10 font-medium text-gray-700">
                {!isLoadingDemoProfiles && "Searching..."}
            </div>
            <div className="grid md:grid-cols-3 md:gap-x-16 gap-y-6 md:gap-y-0 animate-pulse">
                <div className="flex items-center space-x-4">
                    <div className="flex-none rounded-full w-16 h-16 bg-gray-200" />
                    <div className="space-y-1">
                        <div className="w-40 h-5 mb-2 bg-gray-300" />
                        <div className="w-20 h-4 bg-gray-200" />
                        <div className="w-48 h-4 bg-gray-100" />
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="flex-none rounded-full w-16 h-16 bg-gray-200" />
                    <div className="space-y-1">
                        <div className="w-40 h-5 mb-2 bg-gray-300" />
                        <div className="w-20 h-4 bg-gray-200" />
                        <div className="w-48 h-4 bg-gray-100" />
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <div className="flex-none rounded-full w-16 h-16 bg-gray-200" />
                    <div className="space-y-1">
                        <div className="w-40 h-5 mb-2 bg-gray-300" />
                        <div className="w-20 h-4 bg-gray-200" />
                        <div className="w-48 h-4 bg-gray-100" />
                    </div>
                </div>
            </div>
        </div>
    );
};

const SearchResult = ({ space }: { space: Space }) => {
    return (
        <Link href={"/spaces/" + space.id}>
            <a>
                <div className="flex space-x-4 opacity-90 hover:opacity-100 transition-opacity cursor-pointer">
                    <div
                        className={`rounded-full overflow-hidden w-16 h-16 ${
                            space.profilePicture
                                ? "bg-gray-300"
                                : getDefaultColour(space.name)
                        } flex flex-none justify-center items-center text-xl text-oswalds-800`}>
                        {space.profilePicture?.url ? (
                            <Image
                                src={
                                    constants.cdnBaseUrl +
                                    "/" +
                                    space.profilePicture.url
                                }
                                width={64}
                                height={64}
                            />
                        ) : (
                            getInitials(space.name)
                        )}
                    </div>
                    <div>
                        <div className="text-lg font-semibold text-oswalds-800">
                            {space.name}
                        </div>
                        {space.isDemo && (
                            <div className="text-sm text-oswalds-800 font-medium">
                                Example Place
                            </div>
                        )}
                        {(space.yearOfBirth || space.yearOfDeath) && (
                            <div className="text-sm text-gray-700 font-medium">
                                {space.yearOfBirth} – {space.yearOfDeath}
                            </div>
                        )}
                        {space.location && (
                            <div className="text-sm text-gray-500">
                                {space.location}
                            </div>
                        )}
                    </div>
                </div>
            </a>
        </Link>
    );
};

const SearchResults = ({
    results,
    error,
    query,
}: {
    results?: Relay<Space>;
    error?: ApolloError;
    query?: string;
}) => {
    if (error) {
        return (
            <div className="mt-8 text-center text-red-500 font-medium">
                Something went wrong while searching for places.
            </div>
        );
    }

    if (!results) return null;

    const { samplePersonName, samplePersonLink, samplePetName, samplePetLink } = {
        samplePersonName: process.env.NEXT_PUBLIC_SAMPLE_PERSON_NAME,
        samplePersonLink: process.env.NEXT_PUBLIC_SAMPLE_PERSON_LINK as string,
        samplePetName: process.env.NEXT_PUBLIC_SAMPLE_PET_NAME,
        samplePetLink: process.env.NEXT_PUBLIC_SAMPLE_PET_LINK as string,
    };

    return (
        <div>
            <div className="text-center my-10 font-medium text-gray-700">
                {query != "" &&
                    <>
                        We found{" "}
                        <span className="text-loving-500">
                            {results.totalCount || "no"}
                        </span>{" "}
                        results that match your search.
                    </>
                }
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-x-16 gap-y-6 md:gap-y-12">
                {results.edges?.map((edge) => (
                    <SearchResult space={edge.node} key={edge.node.id} />
                ))}
            </div>

            {results.totalCount === 0 && (
                <div className="max-w-prose mx-auto border-t border-b border-gray-200 mt-2 py-8">
                    <div className="text-gray-700 font-medium text-lg mb-1">
                        Be the first to create a place for {query}
                    </div>
                    <p className="text-gray-600 mb-6 text-sm">
                        It only takes a couple of minutes to get started,{" "}
                        <Link href="/spaces/create">
                            <a className="text-loving-500">create a place</a>
                        </Link>{" "}
                        to begin. If you&apos;re looking for inspiration, why not
                        check out some of our featured places linked below?
                    </p>

                    <div className="divide-x divide-gray-200">
                        <Link href={samplePetLink || '#'}>
                            <a className="inline-block font-medium text-sm text-gray-700 hover:text-oswalds-700 pr-8 py-1 transition-colors">
                                <FontAwesomeIcon
                                    icon={faPaw}
                                    size="lg"
                                    className="mr-3 text-oswalds-500"
                                />
                                {samplePetName || 'Peanut'}
                            </a>
                        </Link>
                        <Link href={samplePersonLink || '#'}>
                            <a className="inline-block font-medium text-sm text-gray-700 hover:text-loving-700 pl-8 py-1 transition-colors">
                                <FontAwesomeIcon
                                    icon={faUser}
                                    size="lg"
                                    className="mr-3 text-loving-400"
                                />
                                {samplePersonName || 'John Doe'}
                            </a>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

const filterBuilder = ({
    yearOfBirth,
    location,
    profileType,
}: FilterBuilderProps) => {
    let filter = {};

    if (yearOfBirth) {
        filter = {
            ...filter,
            yearOfBirth: { eq: yearOfBirth },
        };
    }

    if (location) {
        filter = {
            ...filter,
            location: { startsWith: location },
        };
    }

    if (profileType !== ProfileType.All) {
        filter = {
            ...filter,
            profileType: { eq: profileType },
        };
    }

    const filterLength = Object.keys(filter).length;

    // The GraphQL query will error if we pass an empty object as the filter
    // If no filters should be applied, return null instead
    return filterLength > 0 ? filter : null;
};

export const SearchSpaces = () => {
    const [query, setQuery] = useState('');

    const { data, loading, error, refetch, networkStatus } =
        useQuery<{ spaces: Relay<Space> }>(SearchProfiles, {
            variables: {
                nameQuery: "",
                showDemos: true
            },
            notifyOnNetworkStatusChange: true,
        });
    
    // setVariables means we are executing the query with new variables (i.e. we're performing a real search)
    const isLoading = loading || networkStatus === NetworkStatus.setVariables;

    const initialValues: SearchFields = {
        name: "",
        location: "",
        yearOfBirth: "",
        profileType: ProfileType.All,
    };

    const currentYear = new Date(Date.now()).getFullYear();

    const searchValidationSchema = yup.object().shape({
        name: yup
            .string()
            .required("Please enter a name")
            .min(3, "The name must contain at least 3 characters"),
        yearOfBirth: yup
            .number()
            .min(1900, "The year of birth must be at least 1900")
            .max(currentYear, `The year of birth cannot exceed ${currentYear}`),
    });

    const onSearch = async (
        values: SearchFields,
        helpers: FormikHelpers<SearchFields>
    ) => {
        setQuery(values.name);

        refetch({
            nameQuery: values.name,
            showDemos: false,
            filter: filterBuilder({
                yearOfBirth: parseInt(values.yearOfBirth, 10),
                location: values.location,
                profileType: values.profileType,
            }),
        });

        helpers.setSubmitting(false);
    };

    const formik = useFormik({
        initialValues,
        validateOnBlur: false,
        onSubmit: onSearch,
        validationSchema: searchValidationSchema,
    });

    return (
        <div className="container mx-auto max-w-screen-xl">
            <div className="text-2xl font-semibold text-oswalds-800 text-center mb-4">
                Search for a place
            </div>
            <form onSubmit={formik.handleSubmit}>
                <div className="flex flex-wrap flex-col lg:flex-row space-y-6 lg:space-y-0">
                    <div className="flex flex-grow rounded-md border-2 border-gray-200 divide-y-2 divide-gray-200 flex-col lg:flex-row lg:divide-x-2 lg:divide-y-0 lg:rounded-full lg:mr-4">
                        <div className="flex-auto">
                            <input
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                placeholder="Name"
                                className={`w-full bg-gray-50 border-0 rounded-t-md lg:rounded-t-none lg:rounded-l-full px-4 py-4 lg:px-6 font-medium text-sm focus:shadow-lg focus:ring-loving-500 focus:ring-2 focus:bg-white focus:z-10 relative ${
                                    formik.errors.name &&
                                    formik.touched.name &&
                                    "bg-red-50 placeholder-red-400 ring-2 ring-red-300"
                                }`}
                            />
                        </div>
                        <div className="flex-auto">
                            <input
                                type="text"
                                name="location"
                                value={formik.values.location}
                                onChange={formik.handleChange}
                                placeholder="Location"
                                className={`w-full bg-gray-50 border-0 px-4 py-4 font-medium text-sm focus:shadow-lg focus:ring-loving-500 focus:border-loving-500 focus:ring-2 focus:bg-white focus:z-10 relative ${
                                    formik.errors.location &&
                                    formik.touched.location &&
                                    "bg-red-50 placeholder-red-400 ring-2 ring-red-300"
                                }`}
                            />
                        </div>
                        <div className="flex-initial flex-col divide-gray-200 divide-x-2 w-full lg:w-96">
                            <input
                                type="number"
                                max={currentYear}
                                min={1900}
                                name="yearOfBirth"
                                value={formik.values.yearOfBirth}
                                onChange={formik.handleChange}
                                placeholder="Year of birth"
                                className={`w-1/2 bg-gray-50 border-0 rounded-bl-md lg:rounded-bl-none px-4 py-4 font-medium text-sm focus:shadow-lg focus:ring-loving-500 focus:border-loving-500 focus:ring-2 focus:bg-white focus:z-10 relative ${
                                    formik.errors.yearOfBirth &&
                                    formik.touched.yearOfBirth &&
                                    "bg-red-50 placeholder-red-400 ring-2 ring-red-300"
                                }`}
                            />
                            <select
                                name="profileType"
                                value={formik.values.profileType}
                                onChange={formik.handleChange}
                                aria-label="Profile type"
                                className={`w-1/2 bg-gray-50 border-0 rounded-br-md lg:rounded-r-full pr-16 py-4 font-medium text-sm focus:shadow-lg focus:ring-loving-500 focus:border-loving-500 focus:ring-2 focus:bg-white focus:z-10 relative ${
                                    formik.errors.profileType &&
                                    formik.touched.profileType &&
                                    "bg-red-50 placeholder-red-400 ring-2 ring-red-300"
                                }`}>
                                <option value={ProfileType.All}>All</option>
                                <option value={ProfileType.Person}>
                                    People
                                </option>
                                <option value={ProfileType.Pet}>Pets</option>
                            </select>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="btn-lg btn-primary px-12 lg:px-6 mx-auto disabled:opacity-75 disabled:cursor-not-allowed"
                        disabled={formik.isSubmitting || !formik.isValid || loading}>
                        Search
                    </button>
                </div>
                {!formik.isValid && (
                    <div className="bg-red-100 rounded-md p-4 text-xs font-medium text-red-700 space-y-1 mt-4 ">
                        {formik.errors.name && <p>{formik.errors.name}</p>}
                        {formik.errors.location && (
                            <p>{formik.errors.location}</p>
                        )}
                        {formik.errors.yearOfBirth && (
                            <p>{formik.errors.yearOfBirth}</p>
                        )}
                        {formik.errors.profileType && (
                            <p>{formik.errors.profileType}</p>
                        )}
                    </div>
                )}
            </form>
            {isLoading
                ? <SearchResultsSkeleton isLoadingDemoProfiles={query == ""} />
                : <SearchResults results={data?.spaces} error={error} query={query} />
            }
        </div>
    );
};
